import { createFileRoute } from '@tanstack/react-router';
import { type CSSProperties, type FormEvent, useMemo, useState } from 'react';

import type { AssetAttachment, ChartOfAccount } from '@dcc-erp/shared';
import { CreateFixedAssetSchema, fmt, fmtB, fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { BranchSelect, DepartmentSelect, DimensionFilter } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  SpreadsheetImportModal,
  type ImportFieldSpec,
  type ImportResult,
  type ParsedUnit,
  type PreviewColumn,
} from '../components/SpreadsheetImportModal';
import {
  useAssetAttachments,
  useAttachmentDownloadUrl,
  useDeleteAssetAttachment,
  useUploadAssetAttachment,
} from '../lib/queries/asset-attachments';
import { useAssetCategories } from '../lib/queries/asset-categories';
import { useBranches } from '../lib/queries/branches';
import { useCoa } from '../lib/queries/coa';
import { useActiveCompany, useActiveCompanyId } from '../lib/queries/companies';
import { useDepartments } from '../lib/queries/departments';
import {
  type CapitalizeReceipt,
  type CreateFixedAsset,
  type FixedAssetListRow,
  type PendingReceiptRow,
  useCreateFixedAsset,
  useDisposeFixedAsset,
  useFaReconciliation,
  useFixedAssets,
  usePendingReceipts,
  useReceiveAsset,
  useRunDepreciation,
} from '../lib/queries/fixed-assets';
import {
  type CellValue,
  type ExportColumn,
  exportRows,
  headerIndexer,
  todayStamp,
} from '../lib/xlsx';

import type { UseMutationResult } from '@tanstack/react-query';

type TabKey = 'register' | 'depreciation' | 'movement' | 'recon';
type AssetCat = 'building' | 'machine' | 'vehicle' | 'computer' | 'software' | 'furniture';
type AssetStatus = 'active' | 'disposing' | 'fullydep';

// Display shape used by the prototype DOM. We adapt the API row to this on
// render so the JSX below is a 1:1 mirror of page-fixedasset.jsx.
interface Asset {
  id: string;
  name: string;
  cat: AssetCat;
  catTh: string;
  dept: string;
  acquired: string;
  cost: number;
  salvage: number;
  // Useful life in YEARS (display unit per the prototype). API stores months.
  life: number;
  method: 'straight';
  accDep: number;
  nbv: number;
  status: AssetStatus;
  loc: string;
  // Raw asset UUID (for dispose action). Optional so the seeded fallback works.
  _uuid?: string;
}

interface CatBucket {
  name: string;
  count: number;
  cost: number;
  accDep: number;
  nbv: number;
}

interface ScheduleRow {
  month: string;
  open: number;
  dep: number;
  close: number;
  nbv: number;
}

const CAT_COLORS: Readonly<Record<AssetCat, string>> = {
  building: '#1a56a6',
  machine: '#b42318',
  vehicle: '#b54708',
  computer: '#117a3d',
  software: '#7030A0',
  furniture: '#52525b',
};

// Map AssetMaster type / category-code free text → display cat key.
const CAT_FROM_CODE: Readonly<Record<string, AssetCat>> = {
  BUILDING: 'building',
  MACHINE: 'machine',
  VEHICLE: 'vehicle',
  COMPUTER: 'computer',
  SOFTWARE: 'software',
  FURNITURE: 'furniture',
  LAND: 'building',
  SIGNAGE: 'building',
  OTHER: 'furniture',
};

const CAT_DEFAULT_LABEL: Readonly<Record<AssetCat, string>> = {
  building: 'อาคารและสิ่งปลูกสร้าง',
  machine: 'เครื่องจักรการผลิต',
  vehicle: 'ยานพาหนะ',
  computer: 'คอมพิวเตอร์ & อุปกรณ์',
  software: 'ซอฟต์แวร์ & ระบบ IT',
  furniture: 'เฟอร์นิเจอร์ & ตกแต่ง',
};

function adaptApiRow(r: FixedAssetListRow): Asset {
  const catKey: AssetCat = CAT_FROM_CODE[r.categoryCode?.toUpperCase() ?? ''] ?? 'furniture';
  const dispStatus: AssetStatus =
    r.status === 'DISPOSED'
      ? 'disposing'
      : r.status === 'FULLY_DEPRECIATED'
        ? 'fullydep'
        : 'active';
  const cost = Number(r.costAmount);
  const acc = Number(r.accumulatedDepreciation);
  const nbv = Number(r.nbv);
  return {
    id: r.voucherNo,
    name: r.name,
    cat: catKey,
    catTh: r.categoryName ?? CAT_DEFAULT_LABEL[catKey],
    dept: r.branchName ?? '—',
    acquired:
      typeof r.acquisitionDate === 'string'
        ? r.acquisitionDate
        : (r.acquisitionDate as Date).toISOString().slice(0, 10),
    cost,
    salvage: Number(r.salvageValue),
    life: Math.max(1, Math.round(r.usefulLifeMonths / 12)),
    method: 'straight',
    accDep: acc,
    nbv,
    status: dispStatus,
    loc: r.branchName ?? '—',
    _uuid: r.id,
  };
}

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

// ============================================================
// Spreadsheet import/export (นำเข้า / ส่งออก) — one row = one fixed asset.
// Reference fields are code-based (categoryCode / branchCode / departmentCode);
// the import resolves branchCode/departmentCode → uuid via the loaded masters,
// and validates categoryCode against useAssetCategories. Pure create — no
// idempotency key (useCreateFixedAsset.mutateAsync(data) directly).
// ============================================================

// Field-reference table shown on the import dialog's pick stage.
const IMPORT_FIELDS: ImportFieldSpec[] = [
  {
    col: 'code',
    label: 'รหัสภายใน (Asset tag)',
    required: true,
    hint: 'เช่น MACHINE-FBM-270-04 (ห้ามซ้ำ)',
  },
  {
    col: 'name',
    label: 'ชื่อสินทรัพย์',
    required: true,
    hint: 'เช่น เตาเผาอุโมงค์ #4 — Sacmi FBM 270',
  },
  {
    col: 'categoryCode',
    label: 'รหัสหมวดสินทรัพย์',
    required: true,
    hint: 'ต้องมีอยู่จริงในหมวดสินทรัพย์ของบริษัท (ตั้งค่าที่ /settings)',
  },
  {
    col: 'branchCode',
    label: 'รหัสสาขา',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุ ต้องมีอยู่จริง',
  },
  {
    col: 'departmentCode',
    label: 'รหัสหน่วยงาน',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุ ต้องมีอยู่จริง',
  },
  {
    col: 'acquisitionDate',
    label: 'วันที่ได้มา',
    required: true,
    hint: 'รูปแบบ YYYY-MM-DD เช่น 2026-05-29',
  },
  { col: 'costAmount', label: 'ราคาทุน (บาท)', required: true, hint: 'ตัวเลข > 0 เช่น 142400000' },
  {
    col: 'salvageValue',
    label: 'มูลค่าซาก (บาท)',
    required: false,
    hint: 'ตัวเลข ≥ 0 (ค่าเริ่มต้น 0)',
  },
  {
    col: 'usefulLifeMonths',
    label: 'อายุใช้งาน (เดือน)',
    required: true,
    hint: 'จำนวนเต็มบวก เช่น 180 (= 15 ปี)',
  },
  {
    col: 'fundingAccountCode',
    label: 'รหัสบัญชีแหล่งเงิน',
    required: false,
    hint: '2110=เจ้าหนี้การค้า (ค่าเริ่มต้น), 1112=เงินฝากธนาคาร',
  },
];

// Header keys written into the downloadable template — same keys the exporter
// writes (the create-side columns) so a round-trip re-imports cleanly.
const IMPORT_TEMPLATE_COLS: string[] = [
  'code',
  'name',
  'categoryCode',
  'branchCode',
  'departmentCode',
  'acquisitionDate',
  'costAmount',
  'salvageValue',
  'usefulLifeMonths',
  'fundingAccountCode',
];

const IMPORT_TEMPLATE_EXAMPLES: CellValue[][] = [
  [
    'MACHINE-FBM-270-04',
    'เตาเผาอุโมงค์ #4 — Sacmi FBM 270',
    'MACHINE',
    '',
    '',
    '2026-03-12',
    142400000,
    14240000,
    180,
    '2110',
  ],
  [
    'IT-NB-2026-001',
    'Notebook สำนักงาน (10 เครื่อง)',
    'COMPUTER',
    '',
    '',
    '2026-04-18',
    600000,
    60000,
    60,
    '1112',
  ],
];

export const Route = createFileRoute('/fixedasset')({
  component: FixedAssetPage,
});

function FixedAssetPage() {
  const [tab, setTab] = useState<TabKey>('register');
  const [q, setQ] = useState<string>('');
  const [cat, setCat] = useState<string>('all');
  const [filterBranchId, setFilterBranchId] = useState<string>('');
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>('');
  const [period, setPeriod] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  const { data: apiAssets } = useFixedAssets(companyId, {
    ...(filterBranchId ? { branchId: filterBranchId } : {}),
    ...(filterDepartmentId ? { departmentId: filterDepartmentId } : {}),
  });
  const runDep = useRunDepreciation(companyId);
  const disposeMut = useDisposeFixedAsset(companyId);
  const { data: pending = [] } = usePendingReceipts(companyId);
  const receiveMut = useReceiveAsset(companyId);
  const [receiveTarget, setReceiveTarget] = useState<PendingReceiptRow | null>(null);

  // Masters for import/export — code↔id maps for branch/department, and the
  // set of valid category codes (categoryCode is a string key, not a uuid).
  const { data: categories = [] } = useAssetCategories(companyId);
  const { data: branches = [] } = useBranches(companyId);
  const { data: departments = [] } = useDepartments(companyId);
  const createAsset = useCreateFixedAsset(companyId);

  const branchByCode = useMemo(
    () => new Map(branches.map((b) => [b.code.toLowerCase(), b.id])),
    [branches],
  );
  const branchCodeById = useMemo(() => new Map(branches.map((b) => [b.id, b.code])), [branches]);
  const deptByCode = useMemo(
    () => new Map(departments.map((d) => [d.code.toLowerCase(), d.id])),
    [departments],
  );
  const deptCodeById = useMemo(
    () => new Map(departments.map((d) => [d.id, d.code])),
    [departments],
  );
  const categoryCodes = useMemo(
    () => new Set(categories.map((c) => c.code.toLowerCase())),
    [categories],
  );

  // Adapt API rows to the display shape. No seeded fallback — an empty register
  // shows a real empty-state rather than fabricated sample rows.
  const assets: readonly Asset[] = useMemo(() => (apiAssets ?? []).map(adaptApiRow), [apiAssets]);

  const filteredA = useMemo<readonly Asset[]>(
    () =>
      assets.filter((a) => {
        if (cat !== 'all' && a.cat !== cat) return false;
        if (
          q &&
          !(
            a.id.toLowerCase().includes(q.toLowerCase()) ||
            a.name.toLowerCase().includes(q.toLowerCase())
          )
        )
          return false;
        return true;
      }),
    [assets, q, cat],
  );

  const totals = assets.reduce(
    (a, x) => ({
      cost: a.cost + x.cost,
      accDep: a.accDep + x.accDep,
      nbv: a.nbv + x.nbv,
    }),
    { cost: 0, accDep: 0, nbv: 0 },
  );

  const ytdDep = Math.round(totals.cost * 0.071);
  const disposing = assets.filter((a) => a.status === 'disposing').length;

  const byCat: Record<string, CatBucket> = {};
  assets.forEach((a) => {
    const bucket = byCat[a.cat] ?? { name: a.catTh, count: 0, cost: 0, accDep: 0, nbv: 0 };
    bucket.count++;
    bucket.cost += a.cost;
    bucket.accDep += a.accDep;
    bucket.nbv += a.nbv;
    byCat[a.cat] = bucket;
  });

  // Depreciation-schedule preview uses the first asset. Guard against an empty
  // register (0-asset company, or the initial loading render) so the page never
  // crashes on `assets[0]` — fall back to a zeroed placeholder.
  const sel: Asset = assets[0] ?? {
    id: '—',
    name: 'ยังไม่มีสินทรัพย์',
    cat: 'furniture',
    catTh: '—',
    dept: '—',
    acquired: '',
    cost: 0,
    salvage: 0,
    life: 1,
    method: 'straight',
    accDep: 0,
    nbv: 0,
    status: 'active',
    loc: '—',
  };
  const monthlyDep = Math.round((sel.cost - sel.salvage) / (sel.life * 12));
  const schedule: ScheduleRow[] = [];
  let openBal = sel.accDep;
  for (let i = 0; i < 12; i++) {
    const m = new Date(2026, 4 + i, 30);
    openBal += monthlyDep;
    schedule.push({
      month: m.toLocaleDateString('th-TH', { month: 'short', year: '2-digit' }),
      open: openBal - monthlyDep,
      dep: monthlyDep,
      close: openBal,
      nbv: sel.cost - openBal,
    });
  }

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['register', 'ทะเบียนสินทรัพย์'],
    ['depreciation', 'ตารางค่าเสื่อมราคา'],
    ['movement', 'การเคลื่อนไหว / โอน-จำหน่าย'],
    ['recon', 'กระทบยอด GL'],
  ];

  const handleRunDep = (): void => {
    if (!companyId) return;
    const p = window.prompt('งวดค่าเสื่อม (YYYYMM)', period);
    if (!p || !/^\d{6}$/.test(p)) return;
    setPeriod(p);
    runDep.mutate({ period: p });
  };

  const handleDispose = (a: Asset): void => {
    if (!a._uuid || !companyId) return;
    const today = new Date().toISOString().slice(0, 10);
    const amtStr = window.prompt(`มูลค่าจำหน่าย (${a.name})`, '0');
    if (amtStr === null) return;
    const amount = Number(amtStr);
    if (!Number.isFinite(amount) || amount < 0) return;
    disposeMut.mutate({ id: a._uuid, data: { date: today, amount } });
  };

  // ---- ส่งออก (export) -----------------------------------------------------
  // Export the real API rows. Header keys = the
  // import template's create-side columns so a file round-trips, plus read-only
  // computed columns (accumulatedDepreciation / nbv / status) at the end.
  const EXPORT_COLS: ExportColumn<FixedAssetListRow>[] = [
    { header: 'code', value: (r) => r.code, width: 22 },
    { header: 'name', value: (r) => r.name, width: 36 },
    { header: 'categoryCode', value: (r) => r.categoryCode, width: 14 },
    { header: 'branchCode', value: (r) => branchCodeById.get(r.branchId ?? '') ?? '', width: 12 },
    {
      header: 'departmentCode',
      value: (r) => deptCodeById.get(r.departmentId ?? '') ?? '',
      width: 14,
    },
    { header: 'acquisitionDate', value: (r) => String(r.acquisitionDate).slice(0, 10), width: 14 },
    { header: 'costAmount', value: (r) => Number(r.costAmount), width: 16 },
    { header: 'salvageValue', value: (r) => Number(r.salvageValue), width: 14 },
    { header: 'usefulLifeMonths', value: (r) => r.usefulLifeMonths, width: 14 },
    {
      header: 'accumulatedDepreciation',
      value: (r) => Number(r.accumulatedDepreciation),
      width: 18,
    },
    { header: 'nbv', value: (r) => Number(r.nbv), width: 16 },
    { header: 'status', value: (r) => r.status, width: 16 },
  ];

  async function handleExport() {
    const rows = apiAssets ?? [];
    const filename = `สินทรัพย์ถาวร_${company?.code ?? 'COMPANY'}_${todayStamp()}.xlsx`;
    await exportRows(filename, 'สินทรัพย์ถาวร', EXPORT_COLS, rows);
  }

  // ---- นำเข้า (import) -----------------------------------------------------
  function parseImport(grid: string[][]): {
    units: ParsedUnit<CreateFixedAsset>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of [
      'code',
      'name',
      'categoryCode',
      'acquisitionDate',
      'costAmount',
      'usefulLifeMonths',
    ]) {
      if (idx(req) === -1) return { units: [], headerError: `ไม่พบคอลัมน์ที่จำเป็น: ${req}` };
    }
    const units: ParsedUnit<CreateFixedAsset>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;

      // Resolve & validate categoryCode against the company's categories.
      const categoryCode = cell(cols, 'categoryCode');
      if (categoryCode && !categoryCodes.has(categoryCode.toLowerCase())) {
        units.push({ rowNo, data: null, error: `ไม่พบหมวดสินทรัพย์: ${categoryCode}` });
        continue;
      }

      // Resolve optional branchCode / departmentCode → uuid.
      const branchCode = cell(cols, 'branchCode');
      const branchId = branchCode ? branchByCode.get(branchCode.toLowerCase()) : null;
      if (branchCode && !branchId) {
        units.push({ rowNo, data: null, error: `ไม่พบสาขา: ${branchCode}` });
        continue;
      }
      const departmentCode = cell(cols, 'departmentCode');
      const departmentId = departmentCode ? deptByCode.get(departmentCode.toLowerCase()) : null;
      if (departmentCode && !departmentId) {
        units.push({ rowNo, data: null, error: `ไม่พบหน่วยงาน: ${departmentCode}` });
        continue;
      }

      const salvageRaw = cell(cols, 'salvageValue');
      const fundingRaw = cell(cols, 'fundingAccountCode');
      const candidate = {
        code: cell(cols, 'code'),
        name: cell(cols, 'name'),
        categoryCode,
        branchId: branchId ?? null,
        departmentId: departmentId ?? null,
        acquisitionDate: cell(cols, 'acquisitionDate'),
        costAmount: Number(cell(cols, 'costAmount')),
        // Let the schema apply its defaults (salvage 0, funding 2110) when blank.
        ...(salvageRaw ? { salvageValue: Number(salvageRaw) } : {}),
        usefulLifeMonths: Number(cell(cols, 'usefulLifeMonths')),
        ...(fundingRaw ? { fundingAccountCode: fundingRaw } : {}),
      };
      const parsed = CreateFixedAssetSchema.safeParse(candidate);
      units.push(
        parsed.success
          ? // zod widens nullable+optional dims to `string | null | undefined`; the
            // create payload mirror types them as `string | null`. The value is
            // validated, so assert to the payload shape.
            { rowNo, data: parsed.data as CreateFixedAsset, error: null }
          : { rowNo, data: null, error: parsed.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง' },
      );
    }
    return { units, headerError: null };
  }

  async function runImport(
    valid: ParsedUnit<CreateFixedAsset>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await createAsset.mutateAsync(u.data);
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: u.data.code || `แถว ${u.rowNo}`,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  const PREVIEW_COLS: PreviewColumn<CreateFixedAsset>[] = [
    { header: 'รหัส', width: 130, render: (d) => d.code },
    { header: 'ชื่อสินทรัพย์', render: (d) => d.name },
    { header: 'หมวด', width: 100, render: (d) => d.categoryCode },
    { header: 'ราคาทุน', width: 120, render: (d) => fmt(d.costAmount) },
    { header: 'อายุ (เดือน)', width: 90, render: (d) => d.usefulLifeMonths },
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['บัญชีแยกประเภท', 'สินทรัพย์ถาวร']}
        title="สินทรัพย์ถาวร (Fixed Asset Register)"
        actions={
          <>
            <HelpButton guide="fixedasset" />
            <button className="btn" onClick={() => setShowImport(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้า
            </button>
            <button
              className="btn"
              onClick={() => void handleExport()}
              disabled={(apiAssets?.length ?? 0) === 0}
            >
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <button
              className="btn"
              onClick={handleRunDep}
              disabled={!companyId || runDep.isPending}
            >
              <Icon name="refresh" size={14} /> รันค่าเสื่อม
            </button>
            <button className="btn primary" onClick={() => setShowAdd(true)} disabled={!companyId}>
              <Icon name="plus" size={14} /> ลงทะเบียนสินทรัพย์
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ราคาทุนรวม
          </div>
          <div className="value tnum">
            {fmtB(totals.cost)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{assets.length} รายการ · ณ 30 เม.ย. 2026</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            ค่าเสื่อมสะสม
          </div>
          <div className="value tnum">
            {fmtB(totals.accDep)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            {totals.cost > 0 ? Math.round((totals.accDep / totals.cost) * 100) : 0}% ของราคาทุน
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            มูลค่าทางบัญชีสุทธิ
          </div>
          <div className="value tnum">
            {fmtB(totals.nbv)} <span className="unit">บาท</span>
          </div>
          <div className="trend pos">
            <Icon name="arrowUp" size={11} /> +{fmtB(32_400_000)} ได้รับใหม่ YTD
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            ค่าเสื่อม YTD
          </div>
          <div className="value tnum">
            {fmtB(ytdDep)} <span className="unit">บาท</span>
          </div>
          <div className="trend down">{disposing} รายการรอจำหน่าย</div>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'register' && (
        <>
          <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
            <div className="card-head">
              <div>
                <h3>สรุปตามหมวดสินทรัพย์</h3>
                <div className="sub">มูลค่าและสัดส่วนแยกตาม 6 หมวด</div>
              </div>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 14 }}>
                {Object.entries(byCat).map(([k, v]) => (
                  <div
                    key={k}
                    style={{
                      borderLeft: `3px solid ${CAT_COLORS[k as AssetCat]}`,
                      paddingLeft: 12,
                    }}
                  >
                    <div className="faint" style={{ fontSize: 14 }}>
                      {v.name}
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }} className="tnum">
                      {fmtB(v.nbv)}
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 2 }}>
                      {v.count} รายการ · ทุน {fmtB(v.cost)}
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  marginTop: 16,
                  height: 12,
                  display: 'flex',
                  borderRadius: 6,
                  overflow: 'hidden',
                  background: '#f0f0f3',
                }}
              >
                {Object.entries(byCat).map(([k, v]) => (
                  <div
                    key={k}
                    style={{
                      width: `${totals.nbv > 0 ? (v.nbv / totals.nbv) * 100 : 0}%`,
                      background: CAT_COLORS[k as AssetCat],
                    }}
                    title={`${v.name}: ${
                      totals.nbv > 0 ? ((v.nbv / totals.nbv) * 100).toFixed(1) : '0.0'
                    }%`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="tbl-shell">
            <div className="filter-row">
              <input
                className="input search w-lg"
                placeholder="ค้นหา รหัสสินทรัพย์ / ชื่อ"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                style={{
                  paddingLeft: 32,
                  backgroundImage: SEARCH_BG,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: '10px center',
                }}
              />
              <select className="select" value={cat} onChange={(e) => setCat(e.target.value)}>
                <option value="all">หมวด: ทั้งหมด</option>
                <option value="building">อาคารและสิ่งปลูกสร้าง</option>
                <option value="machine">เครื่องจักรการผลิต</option>
                <option value="vehicle">ยานพาหนะ</option>
                <option value="computer">คอมพิวเตอร์ & อุปกรณ์</option>
                <option value="software">ซอฟต์แวร์</option>
                <option value="furniture">เฟอร์นิเจอร์</option>
              </select>
              <DimensionFilter
                companyId={companyId}
                branchId={filterBranchId}
                departmentId={filterDepartmentId}
                onBranchChange={setFilterBranchId}
                onDepartmentChange={setFilterDepartmentId}
              />
              <select className="select">
                <option>สถานะ: ทั้งหมด</option>
                <option>ใช้งาน</option>
                <option>รอจำหน่าย</option>
                <option>เสื่อมเต็มมูลค่า</option>
              </select>
              <select className="select">
                <option>วิธีคิดค่าเสื่อม: ทั้งหมด</option>
                <option>เส้นตรง</option>
                <option>ยอดลดลง</option>
              </select>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button
                  className="btn sm"
                  onClick={() => {
                    /* TODO(phase-3): wire */
                  }}
                >
                  <Icon name="filter" size={12} /> ตัวกรอง
                </button>
              </div>
            </div>
            <div className="tbl-wrap" style={{ maxHeight: 560 }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 24 }}>
                      <input type="checkbox" />
                    </th>
                    <th style={{ width: 130 }}>รหัสสินทรัพย์</th>
                    <th>ชื่อสินทรัพย์</th>
                    <th>หมวด</th>
                    <th>หน่วยงาน</th>
                    <th>วันที่ได้มา</th>
                    <th className="num">ราคาทุน</th>
                    <th className="num">อายุใช้งาน</th>
                    <th className="num">ค่าเสื่อมสะสม</th>
                    <th className="num">มูลค่าสุทธิ (NBV)</th>
                    <th>สถานะ</th>
                    <th>การจัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredA.length === 0 && (
                    <tr>
                      <td
                        colSpan={12}
                        className="muted"
                        style={{ padding: 28, textAlign: 'center' }}
                      >
                        ยังไม่มีทรัพย์สินในทะเบียน — กด “เพิ่มทรัพย์สิน” หรือรับเข้าจากรายการรอรับ
                      </td>
                    </tr>
                  )}
                  {filteredA.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <input type="checkbox" />
                      </td>
                      <td className="code">
                        <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{a.id}</a>
                      </td>
                      <td>{a.name}</td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              background: CAT_COLORS[a.cat],
                            }}
                          />
                          {a.catTh}
                        </span>
                      </td>
                      <td className="muted">{a.dept}</td>
                      <td>{fmtDate(a.acquired)}</td>
                      <td className="num tnum">{fmt(a.cost)}</td>
                      <td className="num">{a.life} ปี</td>
                      <td className="num tnum muted">{fmt(a.accDep)}</td>
                      <td className="num tnum">
                        <b>{fmt(a.nbv)}</b>
                      </td>
                      <td>
                        {a.status === 'active' ? (
                          <span className="badge positive">
                            <span className="bdot" />
                            ใช้งาน
                          </span>
                        ) : a.status === 'disposing' ? (
                          <span className="badge warning">
                            <span className="bdot" />
                            รอจำหน่าย
                          </span>
                        ) : (
                          <span className="badge neutral">
                            <span className="bdot" />
                            เสื่อมเต็ม
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn sm"
                          disabled={!a._uuid || a.status !== 'active' || disposeMut.isPending}
                          onClick={() => handleDispose(a)}
                        >
                          จำหน่าย
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td colSpan={6}>รวม {filteredA.length} รายการ</td>
                    <td className="num tnum">{fmt(filteredA.reduce((a, x) => a + x.cost, 0))}</td>
                    <td />
                    <td className="num tnum">{fmt(filteredA.reduce((a, x) => a + x.accDep, 0))}</td>
                    <td className="num tnum">{fmt(filteredA.reduce((a, x) => a + x.nbv, 0))}</td>
                    <td />
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'depreciation' && (
        <>
          <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
            <div className="card-head">
              <div>
                <h3>
                  ตารางคำนวณค่าเสื่อมราคา — {sel.name} ({sel.id})
                </h3>
                <div className="sub">
                  วิธีเส้นตรง · อายุใช้งาน {sel.life} ปี · ค่าเสื่อมรายเดือน {fmt(monthlyDep)} บาท
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="btn sm"
                  onClick={() => {
                    /* TODO(phase-3): wire */
                  }}
                >
                  <Icon name="print" size={12} /> พิมพ์
                </button>
                <button
                  className="btn sm"
                  onClick={() => {
                    /* TODO(phase-3): wire */
                  }}
                >
                  <Icon name="download" size={12} /> Excel
                </button>
              </div>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 18 }}>
                <div>
                  <div className="faint" style={{ fontSize: 14 }}>
                    ราคาทุน
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }} className="tnum">
                    {fmt(sel.cost)}
                  </div>
                </div>
                <div>
                  <div className="faint" style={{ fontSize: 14 }}>
                    มูลค่าซาก (Salvage)
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }} className="tnum">
                    {fmt(sel.salvage)}
                  </div>
                </div>
                <div>
                  <div className="faint" style={{ fontSize: 14 }}>
                    ฐานคิดค่าเสื่อม
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }} className="tnum">
                    {fmt(sel.cost - sel.salvage)}
                  </div>
                </div>
                <div>
                  <div className="faint" style={{ fontSize: 14 }}>
                    ค่าเสื่อมต่อปี
                  </div>
                  <div
                    style={{ fontSize: 18, fontWeight: 600, marginTop: 4, color: 'var(--brand)' }}
                    className="tnum"
                  >
                    {fmt(monthlyDep * 12)}
                  </div>
                </div>
                <div>
                  <div className="faint" style={{ fontSize: 14 }}>
                    NBV ปัจจุบัน
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }} className="tnum">
                    {fmt(sel.nbv)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="tbl-shell">
            <div className="filter-row">
              <select className="select" style={{ width: 360 }} defaultValue={sel.id}>
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.id} — {a.name}
                  </option>
                ))}
              </select>
              <select className="select">
                <option>ช่วงเวลา: 12 เดือนถัดไป</option>
                <option>24 เดือนถัดไป</option>
                <option>ทั้งอายุใช้งาน</option>
              </select>
            </div>
            <div className="tbl-wrap" style={{ maxHeight: 520 }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>งวด</th>
                    <th>คำอธิบาย</th>
                    <th>เลขที่ JV</th>
                    <th className="num">ค่าเสื่อมสะสม (ยกมา)</th>
                    <th className="num">ค่าเสื่อมงวดนี้</th>
                    <th className="num">ค่าเสื่อมสะสม (ยกไป)</th>
                    <th className="num">มูลค่าสุทธิ (NBV)</th>
                    <th>สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((s, i) => (
                    <tr key={i}>
                      <td>{s.month}</td>
                      <td>บันทึกค่าเสื่อมประจำเดือน — {sel.name}</td>
                      <td className="code muted">
                        {i < 1 ? (
                          <a
                            style={{ color: 'var(--brand)' }}
                          >{`JV-2026-04-DEP${String(i + 82).padStart(3, '0')}`}</a>
                        ) : (
                          <span className="faint">— ยังไม่ออก —</span>
                        )}
                      </td>
                      <td className="num tnum muted">{fmt(s.open)}</td>
                      <td className="num tnum">{fmt(s.dep)}</td>
                      <td className="num tnum">
                        <b>{fmt(s.close)}</b>
                      </td>
                      <td className="num tnum">{fmt(s.nbv)}</td>
                      <td>
                        {i < 1 ? (
                          <span className="badge positive">
                            <span className="bdot" />
                            ผ่านรายการแล้ว
                          </span>
                        ) : (
                          <span className="badge neutral">รอประมวลผล</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td colSpan={4}>รวมค่าเสื่อม 12 เดือนถัดไป</td>
                    <td className="num tnum">{fmt(schedule.reduce((a, s) => a + s.dep, 0))}</td>
                    <td colSpan={3} />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showAdd && companyId && (
        <FixedAssetRegisterModal companyId={companyId} onClose={() => setShowAdd(false)} />
      )}

      {receiveTarget && companyId && (
        <ReceiveAssetModal
          companyId={companyId}
          target={receiveTarget}
          receiveMut={receiveMut}
          onClose={() => setReceiveTarget(null)}
        />
      )}

      {showImport && companyId && (
        <SpreadsheetImportModal<CreateFixedAsset>
          title="นำเข้าสินทรัพย์ถาวร"
          intro={
            <>
              เลือกไฟล์ Excel (.xlsx) หรือ CSV ที่มีหัวคอลัมน์ตามตารางด้านล่าง — หนึ่งแถว =
              สินทรัพย์หนึ่งรายการ. <b>รหัสหมวด/สาขา/หน่วยงาน</b> ต้องตรงกับที่ตั้งค่าไว้ในระบบ.
              แนะนำให้กด “ดาวน์โหลดเทมเพลต” หรือ “ส่งออก” แล้วแก้ในไฟล์นั้นก่อนนำเข้า
            </>
          }
          fields={IMPORT_FIELDS}
          templateColumns={IMPORT_TEMPLATE_COLS}
          templateExamples={IMPORT_TEMPLATE_EXAMPLES}
          templateFilename="fixed_asset_template.xlsx"
          sheetName="สินทรัพย์ถาวร"
          parse={parseImport}
          previewColumns={PREVIEW_COLS}
          runImport={runImport}
          onClose={() => setShowImport(false)}
        />
      )}

      {tab === 'recon' && <ReconTab companyId={companyId} />}

      {tab === 'movement' && (
        <>
          <div className="grid-2-eq">
            <div className="card">
              <div className="card-head">
                <h3>การได้มาของสินทรัพย์ — YTD 2026</h3>
                <span className="badge brand">
                  {pending.length} รายการ ·{' '}
                  {fmtB(pending.reduce((s, p) => s + Number(p.amount), 0))} บาท
                </span>
              </div>
              <div className="card-body flush">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>วันที่</th>
                      <th>รหัส</th>
                      <th>ชื่อสินทรัพย์</th>
                      <th className="num">ราคาทุน</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="muted"
                          style={{ textAlign: 'center', padding: '16px 0' }}
                        >
                          ไม่มีรายการรอรับเข้าทะเบียน
                        </td>
                      </tr>
                    ) : (
                      pending.map((p) => (
                        <tr key={p.journalLineId}>
                          <td>{fmtDate(String(p.voucherDate))}</td>
                          <td className="code">
                            <a style={{ color: 'var(--brand)' }}>{p.voucherNo}</a>
                          </td>
                          <td>
                            {p.description ?? '—'}
                            {p.source === 'AP_BILL' && (
                              <>
                                {' '}
                                <span className="badge info">บิล AP</span>
                              </>
                            )}
                          </td>
                          <td className="num tnum">{fmt(Number(p.amount))}</td>
                          <td>
                            <span className="badge warning">
                              <span className="bdot" />
                              รอตรวจรับ
                            </span>{' '}
                            <button className="btn sm" onClick={() => setReceiveTarget(p)}>
                              รับเข้าทะเบียน
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h3>การจำหน่าย / โอนสินทรัพย์ — YTD</h3>
                <span className="badge negative">2 รายการ</span>
              </div>
              <div className="card-body flush">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>วันที่</th>
                      <th>รหัส</th>
                      <th>ชื่อสินทรัพย์</th>
                      <th className="num">มูลค่าขาย</th>
                      <th className="num">กำไร/ขาดทุน</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{fmtDate('2026-03-12')}</td>
                      <td className="code">FA-2010-000006</td>
                      <td>รถบรรทุก 6 ล้อ Isuzu (เก่า)</td>
                      <td className="num tnum">{fmt(220_000)}</td>
                      <td className="num tnum pos">+{fmt(120_000)}</td>
                    </tr>
                    <tr>
                      <td>{fmtDate('2026-04-22')}</td>
                      <td className="code">FA-2011-000008</td>
                      <td>เครื่องตัดเดิม Lamello (เลิกใช้)</td>
                      <td className="num tnum">{fmt(450_000)}</td>
                      <td className="num tnum neg">−{fmt(180_000)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div>
                <h3>ประวัติการเคลื่อนไหวล่าสุด</h3>
                <div className="sub">การโอนระหว่างหน่วยงาน · เปลี่ยนสถานะ · ปรับปรุงมูลค่า</div>
              </div>
            </div>
            <div className="card-body flush">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>วันที่</th>
                    <th>เลขที่อ้างอิง</th>
                    <th>ประเภท</th>
                    <th>สินทรัพย์</th>
                    <th>จาก</th>
                    <th>ไปยัง</th>
                    <th className="num">มูลค่า</th>
                    <th>ผู้บันทึก</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{fmtDate('2026-05-08')}</td>
                    <td className="code">MV-2026-000042</td>
                    <td>
                      <span className="badge info">
                        <span className="bdot" />
                        โอนระหว่างหน่วย
                      </span>
                    </td>
                    <td>Notebook Lot 14 (8 เครื่อง)</td>
                    <td className="muted">บัญชี</td>
                    <td className="muted">ขายในประเทศ</td>
                    <td className="num tnum">{fmt(480_000)}</td>
                    <td>สุดา ว.</td>
                  </tr>
                  <tr>
                    <td>{fmtDate('2026-05-02')}</td>
                    <td className="code">MV-2026-000041</td>
                    <td>
                      <span className="badge warning">
                        <span className="bdot" />
                        เปลี่ยนสถานะ
                      </span>
                    </td>
                    <td>เครื่องผสมน้ำดิน Eirich R24</td>
                    <td className="muted">ใช้งาน</td>
                    <td className="muted">รอจำหน่าย</td>
                    <td className="num tnum">{fmt(4_040_000)}</td>
                    <td>ปรีชา ก.</td>
                  </tr>
                  <tr>
                    <td>{fmtDate('2026-04-22')}</td>
                    <td className="code">MV-2026-000040</td>
                    <td>
                      <span className="badge negative">
                        <span className="bdot" />
                        จำหน่าย
                      </span>
                    </td>
                    <td>เครื่องตัดเดิม Lamello</td>
                    <td className="muted">โรงงาน 1</td>
                    <td className="muted">—</td>
                    <td className="num tnum">{fmt(450_000)}</td>
                    <td>ปรีชา ก.</td>
                  </tr>
                  <tr>
                    <td>{fmtDate('2026-04-15')}</td>
                    <td className="code">MV-2026-000039</td>
                    <td>
                      <span className="badge info">
                        <span className="bdot" />
                        โอนระหว่างหน่วย
                      </span>
                    </td>
                    <td>โต๊ะประชุม + เก้าอี้ 12 ชุด</td>
                    <td className="muted">สนญ. ชั้น 14</td>
                    <td className="muted">สาขาเชียงใหม่</td>
                    <td className="num tnum">{fmt(220_000)}</td>
                    <td>อรอุมา ส.</td>
                  </tr>
                  <tr>
                    <td>{fmtDate('2026-04-08')}</td>
                    <td className="code">MV-2026-000038</td>
                    <td>
                      <span className="badge positive">
                        <span className="bdot" />
                        ลงทะเบียนใหม่
                      </span>
                    </td>
                    <td>ระบบ Conveyor โรงงาน 1</td>
                    <td className="muted">—</td>
                    <td className="muted">โรงงาน 1</td>
                    <td className="num tnum">{fmt(8_800_000)}</td>
                    <td>สมชาย ก.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// ReconTab — GL reconciliation tab: compares fixed-asset register totals
// against GL balances and highlights differences.
// ============================================================

function ReconTab({ companyId }: { companyId: string | undefined }) {
  const { data, isLoading } = useFaReconciliation(companyId);
  if (isLoading)
    return (
      <div className="card">
        <div className="card-body muted" style={{ padding: 24, textAlign: 'center' }}>
          กำลังโหลด…
        </div>
      </div>
    );
  if (!data) return null;
  return (
    <div className="card">
      <div className="card-head">
        <h3>กระทบยอด ทะเบียนทรัพย์สิน ↔ GL</h3>
        {data.pendingCount > 0 && (
          <span className="badge warning">{data.pendingCount} รายการรอรับเข้าทะเบียน</span>
        )}
      </div>
      <div className="card-body flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>รายการ</th>
              <th className="num">ทะเบียน</th>
              <th className="num">GL</th>
              <th className="num">ผลต่าง</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.label}>
                <td>{r.label}</td>
                <td className="num tnum">{fmt(Number(r.register))}</td>
                <td className="num tnum">{fmt(Number(r.gl))}</td>
                <td className="num tnum">
                  <span className={`badge ${Number(r.diff) === 0 ? 'positive' : 'negative'}`}>
                    {fmt(Number(r.diff))}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// ReceiveAssetModal — converts a pending FA_CLEARING line into a registered
// fixed asset. Collects the fields the inbox can't infer (category / life /
// dep-start) while displaying the read-only context from the clearing line.
// Mirrors the FixedAssetRegisterModal visual pattern exactly.
// ============================================================

interface ReceiveAssetModalProps {
  companyId: string;
  target: PendingReceiptRow;
  receiveMut: UseMutationResult<
    unknown,
    Error,
    { journalLineId: string; data: CapitalizeReceipt },
    unknown
  >;
  onClose: () => void;
}

function ReceiveAssetModal({ companyId, target, receiveMut, onClose }: ReceiveAssetModalProps) {
  const { data: categories = [] } = useAssetCategories(companyId);

  const [categoryCode, setCategoryCode] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [name, setName] = useState<string>(target.description ?? '');
  const [lifeStr, setLifeStr] = useState<string>('');
  const [salvageStr, setSalvageStr] = useState<string>('0');
  const [depStart, setDepStart] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);

  const effectiveCategoryCode = categoryCode || (categories[0]?.code ?? '');
  const lifeNum = Number(lifeStr);
  const canSubmit =
    effectiveCategoryCode.length > 0 &&
    code.trim().length > 0 &&
    name.trim().length > 0 &&
    Number.isFinite(lifeNum) &&
    lifeNum > 0 &&
    !receiveMut.isPending;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    if (!canSubmit) {
      setErr('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }
    receiveMut.mutate(
      {
        journalLineId: target.journalLineId,
        data: {
          code: code.trim(),
          name: name.trim(),
          categoryCode: effectiveCategoryCode,
          usefulLifeMonths: lifeNum,
          salvageValue: Number(salvageStr) || 0,
          ...(depStart ? { depreciationStartDate: depStart } : {}),
        },
      },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (e: unknown) => {
          setErr(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด — ไม่สามารถบันทึกได้');
        },
      },
    );
  }

  return (
    <div style={MODAL_OVERLAY} onMouseDown={onClose}>
      <form style={MODAL_BOX} onMouseDown={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        {/* Header */}
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg,#fafbfc 0%,#fff 100%)',
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>รับเข้าทะเบียนสินทรัพย์</h3>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              แปลงรายการรอรับเข้าทะเบียน → ลงทะเบียนสินทรัพย์ถาวร
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="ปิด" title="ปิด">
            <Icon name="x" size={14} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div style={{ padding: 20, display: 'grid', gap: 14, overflowY: 'auto' }}>
          {/* Read-only context from the clearing line */}
          <div style={SECTION_LABEL}>
            <span style={SECTION_BAR} />
            ข้อมูลจากระบบ (อ่านอย่างเดียว)
          </div>
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 12,
              background: '#fafbfc',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 12,
            }}
          >
            <div>
              <div style={FIELD_LABEL}>เลขที่เอกสาร</div>
              <div className="code" style={{ fontSize: 13, fontWeight: 500 }}>
                {target.voucherNo}
              </div>
            </div>
            <div>
              <div style={FIELD_LABEL}>ราคาทุน (บาท)</div>
              <div className="tnum" style={{ fontSize: 13, fontWeight: 600 }}>
                {fmt(Number(target.amount))}
              </div>
            </div>
            <div>
              <div style={FIELD_LABEL}>แหล่งที่มา</div>
              <div style={{ fontSize: 13 }}>
                <span className={`badge ${target.source === 'AP_BILL' ? 'info' : 'neutral'}`}>
                  {target.source === 'AP_BILL' ? 'บิล AP' : 'JV manual'}
                </span>
                {target.sourceRef && (
                  <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                    {target.sourceRef}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Basic info */}
          <div style={SECTION_LABEL}>
            <span style={SECTION_BAR} />
            ข้อมูลพื้นฐาน
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>
              <div style={FIELD_LABEL}>หมวดสินทรัพย์ *</div>
              <select
                className="select"
                value={effectiveCategoryCode}
                onChange={(e) => setCategoryCode(e.target.value)}
                style={{ width: '100%' }}
                required
              >
                {categories.length === 0 && <option value="">— กำลังโหลด —</option>}
                {categories.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.nameTh}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <div style={FIELD_LABEL}>รหัสภายใน (Asset tag) *</div>
              <input
                className="input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="เช่น MACHINE-FBM-270-04"
                required
                style={{ width: '100%' }}
              />
            </label>
          </div>
          <label>
            <div style={FIELD_LABEL}>ชื่อสินทรัพย์ *</div>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ชื่อสินทรัพย์"
              required
              style={{ width: '100%' }}
            />
          </label>

          {/* Depreciation params */}
          <div style={SECTION_LABEL}>
            <span style={SECTION_BAR} />
            อายุการใช้งาน & ค่าเสื่อม
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <label>
              <div style={FIELD_LABEL}>อายุใช้งาน (เดือน) *</div>
              <input
                type="number"
                className="input"
                value={lifeStr}
                onChange={(e) => setLifeStr(e.target.value)}
                placeholder="เช่น 60"
                min={1}
                max={600}
                step={1}
                required
                style={{ width: '100%', textAlign: 'right' }}
              />
            </label>
            <label>
              <div style={FIELD_LABEL}>มูลค่าซาก (บาท)</div>
              <input
                type="number"
                className="input"
                value={salvageStr}
                onChange={(e) => setSalvageStr(e.target.value)}
                placeholder="0"
                min={0}
                step="0.01"
                style={{ width: '100%', textAlign: 'right' }}
              />
            </label>
            <label>
              <div style={FIELD_LABEL}>วันเริ่มคิดค่าเสื่อม (ไม่บังคับ)</div>
              <input
                type="date"
                className="input"
                value={depStart}
                onChange={(e) => setDepStart(e.target.value)}
                style={{ width: '100%' }}
              />
            </label>
          </div>

          {(receiveMut.error ?? err) && (
            <div
              style={{
                background: 'rgba(180,35,24,0.08)',
                color: 'var(--negative,#b42318)',
                padding: '8px 12px',
                borderRadius: 6,
                fontSize: 13,
                display: 'flex',
                gap: 6,
                alignItems: 'center',
              }}
            >
              <Icon name="x" size={12} />
              {err ??
                (receiveMut.error instanceof Error ? receiveMut.error.message : 'เกิดข้อผิดพลาด')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            background: '#fafbfc',
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          }}
        >
          <div className="muted" style={{ fontSize: 11 }}>
            * ฟิลด์บังคับ
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn ghost" onClick={onClose}>
              ยกเลิก
            </button>
            <button type="submit" className="btn primary" disabled={!canSubmit}>
              {receiveMut.isPending ? 'กำลังบันทึก…' : 'รับเข้าทะเบียน'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// FixedAssetRegisterModal — two-step workflow:
//   step 1 ('form')        — form with smart defaults pulled from
//                            AssetCategory (per-company configurable in
//                            /settings/asset-categories). Live preview of
//                            depreciation amounts + JV.
//   step 2 ('attachments') — after the asset is created, the user can
//                            upload images (jpg/png/webp/gif) and
//                            documents (pdf/word/excel/csv/txt) tied to
//                            the new asset's id. Closes when user is done.
// ============================================================

interface FixedAssetRegisterModalProps {
  companyId: string;
  onClose: () => void;
}

interface FundingOption {
  code: string;
  label: string;
  hint: string;
}

// Funding (Cr) side of the acquisition JV. Sourced from the live COA instead of
// hardcoded 2110/1112 codes (which no longer exist under the real 8-digit master).
// We curate two choices: pay-on-credit (AP trade liability) and pay-by-bank
// (cash/bank asset), picking the first matching postable account of each kind.
function deriveFundingOptions(coa: readonly ChartOfAccount[]): FundingOption[] {
  const opts: FundingOption[] = [];
  const apTrade = coa.find(
    (c) => c.isActive && !c.isControl && c.type === 'LIABILITY' && c.subGroup === 'เจ้าหนี้การค้า',
  );
  if (apTrade) {
    opts.push({
      code: apTrade.code,
      label: apTrade.nameTh,
      hint: 'ซื้อเป็นเงินเชื่อ — JV ตั้งเจ้าหนี้',
    });
  }
  const bank = coa.find(
    (c) =>
      c.isActive && !c.isControl && c.type === 'ASSET' && c.subGroup === 'เงินสดและเงินฝากธนาคาร',
  );
  if (bank) {
    opts.push({
      code: bank.code,
      label: bank.nameTh,
      hint: 'จ่ายผ่านธนาคาร — JV หักเงินฝาก',
    });
  }
  return opts;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

// Modal-shell styles shared across both steps so the box doesn't reflow
// during step transitions.
const MODAL_OVERLAY: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 100,
  padding: 20,
  overflowY: 'auto',
};
const MODAL_BOX: CSSProperties = {
  background: '#fff',
  borderRadius: 10,
  width: '100%',
  maxWidth: 720,
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  maxHeight: 'calc(100vh - 40px)',
  display: 'flex',
  flexDirection: 'column',
};
const SECTION_LABEL: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  marginTop: 6,
  marginBottom: 2,
  letterSpacing: 0.2,
};
const SECTION_BAR: CSSProperties = {
  width: 3,
  height: 14,
  background: 'var(--brand)',
  borderRadius: 2,
};
const FIELD_LABEL: CSSProperties = {
  fontSize: 12,
  marginBottom: 4,
  color: 'var(--text-muted)',
};

function FixedAssetRegisterModal({ companyId, onClose }: FixedAssetRegisterModalProps) {
  // Two-step state machine — 'form' first, then 'attachments' after the
  // backend creates the asset and returns its id.
  const [step, setStep] = useState<'form' | 'attachments'>('form');
  const [createdAssetId, setCreatedAssetId] = useState<string | null>(null);
  const [createdVoucherNo, setCreatedVoucherNo] = useState<string | null>(null);

  const createAsset = useCreateFixedAsset(companyId);
  const { data: categories = [] } = useAssetCategories(companyId);
  const { data: coa = [] } = useCoa(companyId);

  // Funding (Cr) options sourced from the live COA — AP-trade + cash/bank.
  const fundingOptions = useMemo(() => deriveFundingOptions(coa), [coa]);

  // Form state. `categoryCode` is the user's explicit pick; it defaults to
  // '' until they choose. The component derives the *effective* category
  // from `categoryCode || categories[0]?.code` so that defaults like
  // life + salvage flow through even before the dropdown has been
  // touched — avoids a setState-in-effect cascade.
  const [categoryCode, setCategoryCode] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [acquisitionDate, setAcquisitionDate] = useState<string>(todayISO());
  const [depStartDate, setDepStartDate] = useState<string>(todayISO());
  const [depStartTouched, setDepStartTouched] = useState(false);
  const [costStr, setCostStr] = useState<string>('');
  // Raw user input — null sentinels mean "untouched, use category default".
  const [salvageStrRaw, setSalvageStrRaw] = useState<string | null>(null);
  const [lifeMonthsRaw, setLifeMonthsRaw] = useState<number | null>(null);
  // '' until the user picks; the effective code falls back to the first COA-sourced
  // funding option so the JV preview + payload always carry a real account.
  const [fundingAccountCode, setFundingAccountCode] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);

  // Effective category — falls back to the first row when user hasn't picked.
  const effectiveCategoryCode = categoryCode || (categories[0]?.code ?? '');
  const selectedCategory = useMemo(
    () => categories.find((c) => c.code === effectiveCategoryCode),
    [categories, effectiveCategoryCode],
  );

  // Derived values for life + salvage — user-typed value wins; otherwise
  // pulls the default from the effective category.
  const lifeMonths = lifeMonthsRaw ?? selectedCategory?.defaultLifeMonths ?? 60;
  const salvageStr = salvageStrRaw ?? String(selectedCategory?.defaultSalvage ?? 1);
  const lifeTouched = lifeMonthsRaw !== null;
  const salvageTouched = salvageStrRaw !== null;

  const setLifeMonths = (v: number) => setLifeMonthsRaw(v);
  const setSalvageStr = (v: string) => setSalvageStrRaw(v);

  const onCategoryChange = (newCode: string) => {
    setCategoryCode(newCode);
  };

  const onAcquisitionDateChange = (raw: string) => {
    setAcquisitionDate(raw);
    if (!depStartTouched) setDepStartDate(raw);
  };

  const cost = Number(costStr.replace(/,/g, ''));
  const salvage = Number(salvageStr.replace(/,/g, ''));
  const lifeYears = Math.max(1, Math.round(lifeMonths / 12));
  const depBase =
    Number.isFinite(cost) && Number.isFinite(salvage) ? Math.max(0, cost - salvage) : 0;
  const monthlyDep = lifeMonths > 0 ? Math.round(depBase / lifeMonths) : 0;
  const yearlyDep = monthlyDep * 12;

  const codeValid = code.trim().length > 0;
  const nameValid = name.trim().length > 0;
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(acquisitionDate);
  const costValid = Number.isFinite(cost) && cost > 0;
  const salvageValid = Number.isFinite(salvage) && salvage >= 0 && salvage <= cost;
  const lifeValid = Number.isFinite(lifeMonths) && lifeMonths > 0;
  const categoryValid = effectiveCategoryCode.length > 0;
  const canSubmit =
    categoryValid &&
    codeValid &&
    nameValid &&
    dateValid &&
    costValid &&
    salvageValid &&
    lifeValid &&
    !createAsset.isPending;

  // Effective funding code — user pick wins; otherwise the first COA option.
  const effectiveFundingCode = fundingAccountCode || (fundingOptions[0]?.code ?? '');
  const funding = fundingOptions.find((f) => f.code === effectiveFundingCode) ?? fundingOptions[0];
  // GL Dr account for the acquisition JV preview — pulled from the selected
  // category's configured cost account, with the PAE 1210 fallback.
  const drAccount = selectedCategory?.glCostCode ?? '1210';

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    if (!canSubmit) {
      setErr('กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง');
      return;
    }
    const payload: CreateFixedAsset = {
      code: code.trim(),
      name: name.trim(),
      categoryCode: effectiveCategoryCode,
      branchId: branchId || null,
      departmentId: departmentId || null,
      acquisitionDate,
      depreciationStartDate: depStartDate,
      costAmount: cost,
      salvageValue: salvage,
      usefulLifeMonths: lifeMonths,
      depreciationMethod: 'STRAIGHT_LINE',
      fundingAccountCode: effectiveFundingCode,
    };
    try {
      const result = await createAsset.mutateAsync(payload);
      setCreatedAssetId(result.id);
      setCreatedVoucherNo(result.voucherNo);
      setStep('attachments');
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด — ไม่สามารถบันทึกได้');
    }
  }

  // ============================================================
  // Step 2 — attachments view. Reuses the same modal box so the
  // transition is seamless after the asset is created.
  // ============================================================
  if (step === 'attachments' && createdAssetId) {
    return (
      <AttachmentsStep
        companyId={companyId}
        assetId={createdAssetId}
        voucherNo={createdVoucherNo ?? ''}
        assetName={name}
        onDone={onClose}
      />
    );
  }

  return (
    <div style={MODAL_OVERLAY} onMouseDown={onClose}>
      <form
        style={MODAL_BOX}
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg,#fafbfc 0%,#fff 100%)',
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>ลงทะเบียนสินทรัพย์ใหม่</h3>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              ขั้นตอน 1 / 2 — กรอกข้อมูลและบันทึก JV ตั้งต้น
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="ปิด" title="ปิด">
            <Icon name="x" size={14} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div style={{ padding: 20, display: 'grid', gap: 14, overflowY: 'auto' }}>
          {/* Section 1: basic info */}
          <div style={SECTION_LABEL}>
            <span style={SECTION_BAR} />
            ข้อมูลพื้นฐาน
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>
              <div style={FIELD_LABEL}>หมวดสินทรัพย์ *</div>
              <select
                className="select"
                value={effectiveCategoryCode}
                onChange={(e) => onCategoryChange(e.target.value)}
                style={{ width: '100%' }}
                required
              >
                {categories.length === 0 && <option value="">— กำลังโหลด —</option>}
                {categories.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.nameTh}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <div style={FIELD_LABEL}>รหัสภายใน (Asset tag) *</div>
              <input
                className="input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="เช่น MACHINE-FBM-270-04"
                required
                style={{ width: '100%' }}
              />
            </label>
          </div>
          <label>
            <div style={FIELD_LABEL}>ชื่อสินทรัพย์ *</div>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="เช่น เตาเผาอุโมงค์ #4 — Sacmi FBM 270"
              required
              style={{ width: '100%' }}
            />
          </label>

          {/* Section 2: location */}
          <div style={SECTION_LABEL}>
            <span style={SECTION_BAR} />
            ที่ตั้ง & หน่วยงาน
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>
              <div style={FIELD_LABEL}>สาขา (ไม่บังคับ)</div>
              <BranchSelect
                companyId={companyId}
                value={branchId}
                onChange={setBranchId}
                style={{ width: '100%' }}
              />
            </label>
            <label>
              <div style={FIELD_LABEL}>หน่วยงาน (ไม่บังคับ)</div>
              <DepartmentSelect
                companyId={companyId}
                value={departmentId}
                onChange={setDepartmentId}
                branchId={branchId}
                style={{ width: '100%' }}
              />
            </label>
          </div>

          {/* Section 3: cost & life */}
          <div style={SECTION_LABEL}>
            <span style={SECTION_BAR} />
            ราคาทุน & อายุการใช้งาน
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>
              <div style={FIELD_LABEL}>วันที่ได้มา *</div>
              <input
                type="date"
                className="input"
                value={acquisitionDate}
                onChange={(e) => onAcquisitionDateChange(e.target.value)}
                required
                style={{ width: '100%' }}
              />
            </label>
            <label>
              <div style={FIELD_LABEL}>
                วันเริ่มคิดค่าเสื่อม *{' '}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                  {depStartTouched ? '' : '· ผูกกับวันที่ได้มา'}
                </span>
              </div>
              <input
                type="date"
                className="input"
                value={depStartDate}
                onChange={(e) => {
                  setDepStartTouched(true);
                  setDepStartDate(e.target.value);
                }}
                required
                min={acquisitionDate}
                style={{ width: '100%' }}
              />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
            <label>
              <div style={FIELD_LABEL}>วิธีคิดค่าเสื่อม</div>
              <select className="select" disabled value="straight" style={{ width: '100%' }}>
                <option value="straight">เส้นตรง (Straight-line)</option>
              </select>
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px', gap: 12 }}>
            <label>
              <div style={FIELD_LABEL}>ราคาทุน (บาท) *</div>
              <input
                type="number"
                className="input"
                value={costStr}
                onChange={(e) => setCostStr(e.target.value)}
                placeholder="0.00"
                min={0}
                step="0.01"
                required
                style={{ width: '100%', textAlign: 'right' }}
              />
            </label>
            <label>
              <div style={FIELD_LABEL}>
                มูลค่าซาก (บาท){' '}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                  {salvageTouched ? '' : `· default ฿${selectedCategory?.defaultSalvage ?? 1}`}
                </span>
              </div>
              <input
                type="number"
                className="input"
                value={salvageStr}
                onChange={(e) => setSalvageStr(e.target.value)}
                placeholder="1"
                min={0}
                step="0.01"
                style={{ width: '100%', textAlign: 'right' }}
              />
            </label>
            <label>
              <div style={FIELD_LABEL}>
                อายุใช้งาน (เดือน) *{' '}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                  {lifeTouched ? '' : '· จากหมวด'}
                </span>
              </div>
              <input
                type="number"
                className="input"
                value={lifeMonths}
                onChange={(e) => setLifeMonths(Number(e.target.value))}
                min={1}
                max={600}
                step={1}
                required
                style={{ width: '100%', textAlign: 'right' }}
              />
            </label>
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            ≈ {lifeYears} ปี
          </div>

          {/* Section 4: funding */}
          <div style={SECTION_LABEL}>
            <span style={SECTION_BAR} />
            การชำระเงิน (สำหรับ JV ตั้งต้น)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {fundingOptions.length === 0 && (
              <div className="muted" style={{ fontSize: 12, gridColumn: '1 / -1' }}>
                กำลังโหลดบัญชีแหล่งเงินจากผังบัญชี…
              </div>
            )}
            {fundingOptions.map((opt) => {
              const active = effectiveFundingCode === opt.code;
              return (
                <label
                  key={opt.code}
                  style={{
                    border: `1px solid ${active ? 'var(--brand)' : 'var(--border)'}`,
                    background: active ? 'rgba(26,86,166,0.05)' : '#fff',
                    borderRadius: 8,
                    padding: 10,
                    cursor: 'pointer',
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                  }}
                >
                  <input
                    type="radio"
                    name="funding"
                    value={opt.code}
                    checked={active}
                    onChange={() => setFundingAccountCode(opt.code)}
                    style={{ marginTop: 3 }}
                  />
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>
                      {opt.code} — {opt.label}
                    </div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {opt.hint}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Live preview */}
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 12,
              background: '#fafbfc',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              <Icon name="refresh" size={12} />
              สรุปการคำนวณ
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>
                  ฐานคิดค่าเสื่อม
                </div>
                <div className="tnum" style={{ fontSize: 15, fontWeight: 600 }}>
                  {fmt(depBase)} บ.
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>
                  ค่าเสื่อม/เดือน
                </div>
                <div className="tnum" style={{ fontSize: 15, fontWeight: 600 }}>
                  {fmt(monthlyDep)} บ.
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>
                  ค่าเสื่อม/ปี
                </div>
                <div
                  className="tnum"
                  style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand)' }}
                >
                  {fmt(yearlyDep)} บ.
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 12,
                paddingTop: 10,
                borderTop: '1px dashed var(--border)',
                fontSize: 12,
              }}
            >
              <div
                className="muted"
                style={{ fontSize: 11, marginBottom: 6, textTransform: 'uppercase' }}
              >
                Journal Voucher ที่จะถูกบันทึก
              </div>
              <div style={{ display: 'grid', gap: 4, fontFamily: 'var(--font-mono, monospace)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    <b>Dr</b> {drAccount} —{' '}
                    {selectedCategory
                      ? 'จากหมวด ' + selectedCategory.nameTh
                      : 'ที่ดิน อาคาร อุปกรณ์'}
                  </span>
                  <span className="tnum">{fmt(costValid ? cost : 0)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    <b>Cr</b> {funding ? `${funding.code} — ${funding.label}` : 'แหล่งเงิน'}
                  </span>
                  <span className="tnum">{fmt(costValid ? cost : 0)}</span>
                </div>
              </div>
            </div>
          </div>

          {err && (
            <div
              style={{
                background: 'rgba(180,35,24,0.08)',
                color: 'var(--negative,#b42318)',
                padding: '8px 12px',
                borderRadius: 6,
                fontSize: 13,
                display: 'flex',
                gap: 6,
                alignItems: 'center',
              }}
            >
              <Icon name="x" size={12} />
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            background: '#fafbfc',
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          }}
        >
          <div className="muted" style={{ fontSize: 11 }}>
            * ฟิลด์บังคับ · ขั้นตอนถัดไป: แนบรูป + ไฟล์ประกอบ
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn ghost" onClick={onClose}>
              ยกเลิก
            </button>
            <button type="submit" className="btn primary" disabled={!canSubmit}>
              {createAsset.isPending ? (
                'กำลังบันทึก…'
              ) : (
                <>
                  บันทึก & ไปต่อ <Icon name="chevron" size={12} />
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// AttachmentsStep — step 2 of the register modal. Lets users upload
// images (jpg/png/webp/gif) and supporting documents (pdf/word/excel/
// csv/txt) tied to the just-created asset. Max 10MB per file. Images
// render as thumbnails; documents render as a list with download.
// ============================================================

interface AttachmentsStepProps {
  companyId: string;
  assetId: string;
  voucherNo: string;
  assetName: string;
  onDone: () => void;
}

function AttachmentsStep({
  companyId,
  assetId,
  voucherNo,
  assetName,
  onDone,
}: AttachmentsStepProps) {
  const { data: attachments = [] } = useAssetAttachments(companyId, assetId);
  const uploadMut = useUploadAssetAttachment(companyId, assetId);
  const deleteMut = useDeleteAssetAttachment(companyId, assetId);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  const images = attachments.filter((a) => a.kind === 'IMAGE');
  const documents = attachments.filter((a) => a.kind === 'DOCUMENT');

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploadErr(null);
    for (const f of Array.from(files)) {
      if (f.size > 10 * 1024 * 1024) {
        setUploadErr(`ไฟล์ "${f.name}" ใหญ่เกิน 10MB`);
        continue;
      }
      try {
        await uploadMut.mutateAsync({
          file: f,
          kind: f.type.startsWith('image/') ? 'IMAGE' : 'DOCUMENT',
        });
      } catch (e: unknown) {
        setUploadErr(e instanceof Error ? e.message : `อัปโหลด "${f.name}" ไม่สำเร็จ`);
      }
    }
  }

  return (
    <div style={MODAL_OVERLAY} onMouseDown={onDone}>
      <div style={MODAL_BOX} onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg,#fafbfc 0%,#fff 100%)',
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>แนบรูป + ไฟล์ประกอบ</h3>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              ขั้นตอน 2 / 2 — สินทรัพย์ {voucherNo} ลงทะเบียนแล้ว
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onDone} aria-label="ปิด" title="ปิด">
            <Icon name="x" size={14} />
          </button>
        </div>

        {/* Success banner */}
        <div
          style={{
            margin: '14px 20px 0',
            padding: '10px 14px',
            background: 'rgba(17,122,61,0.08)',
            border: '1px solid rgba(17,122,61,0.2)',
            borderRadius: 8,
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            fontSize: 13,
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: 'var(--positive,#117a3d)',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name="check" size={12} />
          </span>
          <div>
            <div style={{ fontWeight: 500 }}>บันทึกสินทรัพย์เรียบร้อย</div>
            <div className="muted" style={{ fontSize: 11, marginTop: 1 }}>
              {assetName} · JV ตั้งต้นโพสต์เข้าระบบแล้ว
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 20, display: 'grid', gap: 16, overflowY: 'auto' }}>
          {/* Drop zone */}
          <UploadDropZone
            onFiles={(files) => {
              void handleFiles(files);
            }}
            isUploading={uploadMut.isPending}
          />

          {uploadErr && (
            <div
              style={{
                background: 'rgba(180,35,24,0.08)',
                color: 'var(--negative,#b42318)',
                padding: '8px 12px',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {uploadErr}
            </div>
          )}

          {/* Image grid */}
          <div>
            <div style={SECTION_LABEL}>
              <span style={SECTION_BAR} />
              รูปประกอบ ({images.length})
            </div>
            {images.length === 0 ? (
              <div
                className="muted"
                style={{
                  fontSize: 12,
                  padding: 16,
                  textAlign: 'center',
                  border: '1px dashed var(--border)',
                  borderRadius: 8,
                  marginTop: 8,
                }}
              >
                ยังไม่มีรูป — ลากภาพมาวางในกล่องด้านบนได้
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: 10,
                  marginTop: 8,
                }}
              >
                {images.map((a) => (
                  <AttachmentThumb
                    key={a.id}
                    companyId={companyId}
                    assetId={assetId}
                    attachment={a}
                    onDelete={() => deleteMut.mutate(a.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Document list */}
          <div>
            <div style={SECTION_LABEL}>
              <span style={SECTION_BAR} />
              ไฟล์ประกอบ ({documents.length})
            </div>
            {documents.length === 0 ? (
              <div
                className="muted"
                style={{
                  fontSize: 12,
                  padding: 16,
                  textAlign: 'center',
                  border: '1px dashed var(--border)',
                  borderRadius: 8,
                  marginTop: 8,
                }}
              >
                ยังไม่มีไฟล์ประกอบ (ใบกำกับภาษี / สัญญารับประกัน / คู่มือ ฯลฯ)
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                {documents.map((a) => (
                  <AttachmentRow
                    key={a.id}
                    companyId={companyId}
                    assetId={assetId}
                    attachment={a}
                    onDelete={() => deleteMut.mutate(a.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            background: '#fafbfc',
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          }}
        >
          <div className="muted" style={{ fontSize: 11 }}>
            ไฟล์สูงสุด 10MB ต่อไฟล์ · รูป (jpg/png/webp/gif) · เอกสาร (pdf/doc/xls/csv/txt)
          </div>
          <button type="button" className="btn primary" onClick={onDone}>
            <Icon name="check" size={12} /> เสร็จสิ้น
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// UploadDropZone — drag-and-drop file picker. Click-to-browse falls
// back to a hidden file input for keyboard / accessibility.
// ============================================================

function UploadDropZone({
  onFiles,
  isUploading,
}: {
  onFiles: (files: FileList | null) => void;
  isUploading: boolean;
}) {
  const [dragOver, setDragOver] = useState(false);
  const inputId = 'fa-upload-input';

  return (
    <label
      htmlFor={inputId}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onFiles(e.dataTransfer.files);
      }}
      style={{
        display: 'block',
        cursor: 'pointer',
        textAlign: 'center',
        padding: 24,
        border: `2px dashed ${dragOver ? 'var(--brand)' : 'var(--border)'}`,
        background: dragOver ? 'rgba(26,86,166,0.04)' : '#fafbfc',
        borderRadius: 10,
        transition: 'all 0.15s',
      }}
    >
      <input
        id={inputId}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
        onChange={(e) => onFiles(e.target.files)}
        style={{ display: 'none' }}
      />
      <Icon name="upload" size={20} />
      <div style={{ marginTop: 8, fontSize: 14, fontWeight: 500 }}>
        {isUploading ? 'กำลังอัปโหลด…' : 'ลากไฟล์มาวางที่นี่ หรือคลิกเพื่อเลือก'}
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        รูป + ไฟล์เอกสาร เลือกหลายไฟล์ได้พร้อมกัน
      </div>
    </label>
  );
}

// ============================================================
// AttachmentThumb — image tile with delete on hover.
// ============================================================

function AttachmentThumb({
  companyId,
  assetId,
  attachment,
  onDelete,
}: {
  companyId: string;
  assetId: string;
  attachment: AssetAttachment;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  // Presigned URL fetched lazily when the thumb mounts; refreshes before
  // server-side expiry (4-min staleTime, 5-min server expiry).
  const url = usePresignedUrl(companyId, assetId, attachment.id);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        aspectRatio: '1 / 1',
        borderRadius: 8,
        overflow: 'hidden',
        background: '#f0f0f3',
        border: '1px solid var(--border)',
      }}
    >
      {url ? (
        <img
          src={url}
          alt={attachment.filename}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--text-muted)',
            fontSize: 11,
          }}
        >
          กำลังโหลด…
        </div>
      )}
      {hover && (
        <button
          type="button"
          onClick={onDelete}
          title="ลบรูป"
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(180,35,24,0.9)',
            color: '#fff',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Icon name="x" size={11} />
        </button>
      )}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '4px 6px',
          background: 'rgba(0,0,0,0.55)',
          color: '#fff',
          fontSize: 10,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={attachment.filename}
      >
        {attachment.filename}
      </div>
    </div>
  );
}

// ============================================================
// AttachmentRow — document list row with download + delete.
// ============================================================

function AttachmentRow({
  companyId,
  assetId,
  attachment,
  onDelete,
}: {
  companyId: string;
  assetId: string;
  attachment: AssetAttachment;
  onDelete: () => void;
}) {
  const url = usePresignedUrl(companyId, assetId, attachment.id);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: '#fff',
      }}
    >
      <Icon name="download" size={14} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={attachment.filename}
        >
          {attachment.filename}
        </div>
        <div className="muted" style={{ fontSize: 11 }}>
          {(attachment.sizeBytes / 1024).toFixed(1)} KB · {attachment.mimeType}
        </div>
      </div>
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn sm"
          style={{ textDecoration: 'none' }}
        >
          เปิด
        </a>
      )}
      <button type="button" className="btn sm" onClick={onDelete} title="ลบไฟล์">
        <Icon name="x" size={11} />
      </button>
    </div>
  );
}

// Small wrapper that flattens the useAttachmentDownloadUrl shape into a
// plain URL string for the visual components.
function usePresignedUrl(companyId: string, assetId: string, attachmentId: string): string | null {
  const { data } = useAttachmentDownloadUrl(companyId, assetId, attachmentId, true);
  return data?.url ?? null;
}
